import { LoadMoreButton } from 'components/LoadMoreButton';
import { NewsCard } from 'components/News/NewsCard';
import { useFetchDataByPage } from 'hooks/useFetchDataByPage';
import { useRouter } from 'next/router';
import React, { useCallback, useState } from 'react';
import { useSearchParam } from 'react-use';
import { axiosInstance } from 'services/axios';
import { NewsType } from 'types';

import { BasePath } from '../../constants/basePath';
import { TopPostTitle } from '../common/TopPostTitle';

interface Props {
  latestHotNews: NewsType[];
  news: NewsType[];
  newsTotalCount: number;
}

const DEFAULT_LIMIT = 6;

export const ListNews: React.FC<Props> = ({
  news,
  newsTotalCount,
  latestHotNews
}) => {
  const [page, setPage] = useState(+useSearchParam('page') || 1);
  const [loading, setLoading] = useState<boolean>(false);
  const [newsList, setNewsList] = useState<NewsType[]>(news);
  const shouldShowGetMoreButton = newsList.length < newsTotalCount;
  const router = useRouter();

  const fetchNews = useCallback(
    async (pageNumber: number, limit: number) => {
      const apiUrl = '/api/news';
      const payload = {
        pageNumber,
        limit,
        latestHotNewsIds: latestHotNews.map(hotNews => hotNews.id)
      };
      try {
        const { data } = await axiosInstance.post(apiUrl, payload);
        return data.news;
      } catch (error) {
        console.error(error);
      }
    },
    [latestHotNews]
  );

  useFetchDataByPage(DEFAULT_LIMIT, fetchNews, setNewsList);

  const handleGetMoreNews = async () => {
    setLoading(true);
    try {
      const data = await fetchNews(page + 1, DEFAULT_LIMIT);
      const newNewsList = [...newsList, ...data];
      setNewsList(newNewsList);
      setPage(page + 1);
      router.replace(
        {
          query: { ...router.query, page: page + 1 }
        },
        undefined,
        { shallow: true }
      );
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const [firstNews] = latestHotNews.slice(0, 5);

  return (
    <div className="w-full flex justify-center">
      <div className="w-[1280px] px-4 md:px-[40px] xl:px-[80px]">
        <div
          className="w-full grid grid-cols-1 md:grid-cols-13 mb-[40px]"
          data-testid="list-hottest-news"
        >
          <div className="hidden md:block md:col-span-8">
            {firstNews && (
              <NewsCard
                news={firstNews}
                isHottestNews={true}
                imageWidth="700"
                imageHeight="400"
              />
            )}
          </div>
          <div className="col-span-1 md:hidden">
            {firstNews && <NewsCard news={firstNews} isHottestNews={true} />}
          </div>
          <div className="col-span-5 relative">
            <TopPostTitle
              title={firstNews.title}
              subTitle={firstNews.sapo}
              basePath={BasePath.NewsBasePath}
              slug={firstNews.slug}
            />
          </div>
        </div>
        <div
          className="grid grid-cols-1 md:grid-cols-9 gap-y-10 gap-x-6"
          data-testid="remain-news-area"
        >
          {newsList.length > 0 &&
            newsList.map(_news => (
              <div key={_news.id} className="col-span-1 md:col-span-3">
                <NewsCard news={_news} />
              </div>
            ))}
        </div>
        {shouldShowGetMoreButton && (
          <div className="flex justify-center mt-[40px]">
            <LoadMoreButton
              loading={loading}
              onClick={handleGetMoreNews}
              dataTestId="get-more-news-button"
            />
          </div>
        )}
      </div>
    </div>
  );
};
